import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Mic, Square, Upload, CheckCircle2, Circle, AlertCircle, FileSignature, Copy, Undo2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { aufgabenAPI, autopflegeAPI, onboardingAPI, sprachnotizAPI } from '@/lib/api';
import StammkurseBlock from '@/components/StammkurseBlock';

/* Lead-/Mitglied-Karte, linke Spalte (Erweiterung v6):
   - „Vertrag abgeschlossen" (M7-Übergangsknopf, bis Vertrag+SEPA aus v4 steht)
   - Onboarding 0–30 mit sieben Schritten + Copilot-Entwurf
   - „Aus Gesprächen": Felder der Auto-Pflege (M9) mit Rückgängig
   - Sprachnotiz (M11): Aufnahme im Browser (max. 3 Min) oder Datei/Text */

const SCHRITT_LABEL = { tag0: 'Tag 0 · Sprachnachricht', tag1: 'Tag 1 · Team-Post', training1: '1. Training · Trainer-Notiz', tag2: 'Tag 2 · Anruf',
  checkup_gebucht: 'Woche 1 · Check-Up buchen', tag14: 'Tag 14 · Fortschritt', tag30: 'Tag 30 · Check-Up + Empfehlung' };
const MAX_SEKUNDEN = 180;

function datum(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function VertragKnopf({ leadId, lead, onChange }) {
  const [offen, setOffen] = useState(false);
  const [ziel, setZiel] = useState(lead?.ziel_satz || '');
  const [kurs, setKurs] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const senden = async () => {
    if (ziel.trim().length < 5) { toast.error('Ziel-Satz ist Pflicht — ein Satz, was das Mitglied erreichen will'); return; }
    setLaeuft(true);
    try {
      const r = await onboardingAPI.vertragAbgeschlossen(leadId, { ziel_satz: ziel.trim(), kurs: kurs.trim() || null });
      toast.success(`Onboarding gestartet — ${r.data.aufgaben_neu} Aufgaben angelegt`);
      setOffen(false); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setLaeuft(false); }
  };
  const navigate = useNavigate();
  // Kunde wird ein Lead nur mit Vertrag (30.10.2026): ohne Vertrag führt der Knopf in den Verkaufsrechner mit dem Lead.
  // Der Server lehnt „Vertrag abgeschlossen“ ohne Vertrag ohnehin ab (409). Mit Vertrag (Stufe Kunde) bleibt es der Ziel-Satz fürs Onboarding.
  const ohneVertrag = !['customer', 'abschluss'].includes(lead?.stage);
  if (ohneVertrag) return (
    <Button size="sm" variant="outline" className="gap-1.5 w-full" onClick={() => navigate(`/verkauf?lead=${encodeURIComponent(leadId)}`)} data-testid="vertrag-abgeschlossen-btn"
            title="Ein Lead wird nur mit Vertrag zum Kunden">
      <FileSignature className="w-4 h-4" /> Zum Verkaufsrechner (Vertrag)
    </Button>
  );
  if (!offen) return <Button size="sm" variant="outline" className="gap-1.5 w-full" onClick={() => setOffen(true)} data-testid="vertrag-abgeschlossen-btn"><FileSignature className="w-4 h-4" /> Vertrag abgeschlossen</Button>;
  return (
    <div className="space-y-2 rounded-md border border-border/60 p-3 bg-muted/20">
      <p className="text-xs text-muted-foreground">Wandelt den Lead zum Mitglied und startet die Onboarding-Kette 0–30 (Aufgaben bei Cagla).</p>
      <Textarea rows={2} value={ziel} onChange={e => setZiel(e.target.value)} placeholder="Ziel in einem Satz (Pflicht) — z. B. „Bis Sommer 8 kg leichter und fit für den Wettkampf“" data-testid="ziel-satz" />
      <Input value={kurs} onChange={e => setKurs(e.target.value)} placeholder="Erster Kurs (optional, z. B. Kids Class 1)" />
      <div className="flex gap-2">
        <Button size="sm" onClick={senden} disabled={laeuft} className="gap-1.5">{laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Onboarding starten</Button>
        <Button size="sm" variant="ghost" onClick={() => setOffen(false)}>Abbrechen</Button>
      </div>
    </div>
  );
}

function OnboardingSchritte({ leadId, onChange }) {
  const [stand, setStand] = useState(null);
  const [offen, setOffen] = useState(null);
  const lade = useCallback(async () => { try { setStand((await onboardingAPI.stand(leadId)).data); } catch { /* still */ } }, [leadId]);
  useEffect(() => { lade(); }, [lade]);
  if (!stand?.onboarding) return null;
  const ob = stand.onboarding;
  const aufgaben = Object.fromEntries((stand.aufgaben || []).map(a => [a.kontext?.schritt, a]));
  const erledigen = async (a) => {
    try { await aufgabenAPI.erledigt(a.id); toast.success('Erledigt'); lade(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
  };
  const kopieren = async (t) => { try { await navigator.clipboard.writeText(t); toast.success('Kopiert'); } catch { toast.error('Kopieren nicht möglich'); } };
  const heute = new Date().toDateString();
  return (
    <Card className="border-border/60" data-testid="onboarding-panel">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center justify-between">Onboarding 0–30
        <span className="text-xs font-normal text-muted-foreground">seit {datum(ob.start)} · {ob.trainings || 0} Trainings</span></CardTitle></CardHeader>
      <CardContent className="space-y-1.5">
        {ob.ziel_satz && <p className="text-xs italic text-muted-foreground">„{ob.ziel_satz}“</p>}
        {Object.keys(SCHRITT_LABEL).map(s => {
          const a = aufgaben[s];
          const st = ob.schritte?.[s];
          const erledigt = st === 'erledigt' || a?.status === 'erledigt';
          const faellig = a?.faellig_am ? new Date(a.faellig_am) : null;
          const ueberfaellig = !erledigt && (st === 'ueberfaellig' || (faellig && faellig < new Date() && faellig.toDateString() !== heute));
          const Icon = erledigt ? CheckCircle2 : ueberfaellig ? AlertCircle : Circle;
          return (
            <div key={s} className="text-xs">
              <button className="flex items-center gap-2 w-full text-left py-0.5" onClick={() => setOffen(o => o === s ? null : s)}>
                <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${erledigt ? 'text-emerald-600' : ueberfaellig ? 'text-red-600' : 'text-muted-foreground'}`} />
                <span className={`flex-1 ${erledigt ? 'line-through text-muted-foreground' : ''}`}>{SCHRITT_LABEL[s]}</span>
                <span className={`text-[10px] ${ueberfaellig ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>{a ? datum(a.faellig_am) : ''}</span>
              </button>
              {offen === s && a && (
                <div className="ml-5 mt-1 mb-2 rounded-md border border-dashed border-border/60 bg-muted/20 p-2 space-y-1.5">
                  {a.beschreibung && <p className="text-muted-foreground">{a.beschreibung}</p>}
                  {a.kontext?.copilot && <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed">{a.kontext.copilot}</pre>}
                  <div className="flex gap-1.5">
                    {a.kontext?.copilot && <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => kopieren(a.kontext.copilot)}><Copy className="w-3 h-3" /> Kopieren</Button>}
                    {a.status === 'offen' && <Button size="sm" className="h-7 text-xs gap-1" onClick={() => erledigen(a)}><CheckCircle2 className="w-3 h-3" /> Erledigt</Button>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function AusGespraechen({ leadId, aktualisierung }) {
  const [daten, setDaten] = useState(null);
  const lade = useCallback(async () => { try { setDaten((await autopflegeAPI.stand(leadId)).data); } catch { /* still */ } }, [leadId]);
  useEffect(() => { lade(); }, [lade, aktualisierung]);
  if (!daten) return null;
  const felder = Object.entries(daten.labels || {}).filter(([k]) => k !== 'naechster_schritt' && daten.felder?.[k]);
  const letzte = (daten.aenderungen || []).slice(0, 3);
  if (!felder.length && !letzte.length) return null;
  const rueck = async (id) => {
    try {
      const r = await autopflegeAPI.rueckgaengig(leadId, id);
      toast[r.data.ok ? 'success' : 'error'](r.data.ok ? 'Rückgängig gemacht' : r.data.grund);
      lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
  };
  return (
    <Card className="border-border/60" data-testid="autopflege-panel">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Aus Gesprächen <span className="text-xs font-normal text-muted-foreground">(Auto-Pflege)</span></CardTitle></CardHeader>
      <CardContent className="space-y-1.5 text-xs">
        {felder.map(([k, l]) => (
          <div key={k} className="flex justify-between gap-2"><span className="text-muted-foreground">{l}</span><span className="font-medium text-right">{daten.felder[k]}</span></div>
        ))}
        {daten.naechster_schritt?.wer === 'autopflege' && (
          <div className="flex justify-between gap-2"><span className="text-muted-foreground">Nächster Schritt</span><span className="font-medium text-right">{daten.naechster_schritt.was} · {datum(daten.naechster_schritt.faellig_am)}</span></div>
        )}
        {letzte.length > 0 && (
          <div className="pt-1 border-t border-border/50 space-y-1">
            {letzte.map(e => (
              <div key={e.id} className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <span className="flex-1 truncate">{datum(e.zeitpunkt)} · {e.details?.label}: „{e.details?.alt || '–'}“ → „{e.details?.neu || '–'}“</span>
                {!e.details?.rueckgaengig_von && e.details?.quelle !== 'rueckgaengig' && (
                  <button className="inline-flex items-center gap-0.5 text-primary" onClick={() => rueck(e.id)} title="Rückgängig"><Undo2 className="w-3 h-3" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Sprachnotiz({ leadId, onChange }) {
  const [nimmtAuf, setNimmtAuf] = useState(false);
  const [sekunden, setSekunden] = useState(0);
  const [sendet, setSendet] = useState(false);
  const [text, setText] = useState('');
  const [textOffen, setTextOffen] = useState(false);
  const recorder = useRef(null);
  const teile = useRef([]);
  const timer = useRef(null);
  const datei = useRef(null);

  const stopp = () => { try { recorder.current?.state !== 'inactive' && recorder.current?.stop(); } catch { /* egal */ } };
  useEffect(() => () => { clearInterval(timer.current); stopp(); }, []);

  const senden = async ({ audio, text: t }) => {
    setSendet(true);
    try {
      const r = await sprachnotizAPI.senden(leadId, { audio, text: t });
      toast.success(`Notiz abgelegt: „${(r.data.text || '').slice(0, 80)}${(r.data.text || '').length > 80 ? '…' : ''}“`);
      setText(''); setTextOffen(false); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Notiz konnte nicht abgelegt werden'); }
    finally { setSendet(false); }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') { toast.error('Aufnahme in diesem Browser nicht möglich — Datei hochladen oder Text eingeben'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const typ = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find(t => MediaRecorder.isTypeSupported?.(t)) || '';
      const rec = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined);
      teile.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) teile.current.push(e.data); };
      rec.onstop = () => {
        clearInterval(timer.current); setNimmtAuf(false);
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(teile.current, { type: rec.mimeType || 'audio/webm' });
        const endung = (rec.mimeType || '').includes('mp4') ? 'm4a' : (rec.mimeType || '').includes('ogg') ? 'ogg' : 'webm';
        const f = new File([blob], `notiz.${endung}`, { type: blob.type });
        if (f.size < 1000) { toast.error('Aufnahme zu kurz'); return; }
        senden({ audio: f });
      };
      recorder.current = rec; rec.start(1000);
      setNimmtAuf(true); setSekunden(0);
      timer.current = setInterval(() => setSekunden(s => { if (s + 1 >= MAX_SEKUNDEN) { stopp(); } return s + 1; }), 1000);
    } catch { toast.error('Mikrofon nicht freigegeben'); }
  };

  return (
    <Card className="border-border/60" data-testid="sprachnotiz-panel">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          {!nimmtAuf ? (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={start} disabled={sendet} data-testid="sprachnotiz-start">
              {sendet ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />} Sprachnotiz
            </Button>
          ) : (
            <Button size="sm" className="gap-1.5 bg-red-600 hover:bg-red-700" onClick={stopp} data-testid="sprachnotiz-stopp">
              <Square className="w-3.5 h-3.5" /> Stopp · {Math.floor(sekunden / 60)}:{String(sekunden % 60).padStart(2, '0')}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => datei.current?.click()} disabled={sendet || nimmtAuf}><Upload className="w-4 h-4" /> Datei</Button>
          <input ref={datei} type="file" accept="audio/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) senden({ audio: f }); e.target.value = ''; }} />
          <button className="text-xs text-muted-foreground underline" onClick={() => setTextOffen(o => !o)}>Text statt Audio</button>
        </div>
        {textOffen && (
          <div className="space-y-1.5">
            <Textarea rows={3} value={text} onChange={e => setText(e.target.value)} placeholder="Was wurde besprochen? Die Auto-Pflege liest die Notiz und füllt die Felder." />
            <Button size="sm" onClick={() => text.trim() && senden({ text: text.trim() })} disabled={sendet || !text.trim()}>Notiz ablegen</Button>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">Max. 3 Minuten. Das Audio wird nach der Transkription verworfen; der Text landet als Telefon-Eintrag im Zeitstrahl.</p>
      </CardContent>
    </Card>
  );
}

export default function OnboardingPanel({ leadId, lead, onChange, darfVertrag, darfBearbeiten }) {
  const [tick, setTick] = useState(0);
  const aendern = () => { setTick(t => t + 1); onChange?.(); };
  const istMitglied = lead?.stage === 'customer' || lead?.stage === 'abschluss' || !!lead?.onboarding;
  return (
    <div className="space-y-3" data-testid="v6-panel">
      {!lead?.onboarding && darfVertrag && <VertragKnopf leadId={leadId} lead={lead} onChange={aendern} />}
      {(istMitglied || lead?.stammkurse?.length || lead?.stammkurs_gruppe) && <StammkurseBlock leadId={leadId} lead={lead} darfBearbeiten={darfBearbeiten} onChange={onChange} />}
      {(istMitglied || lead?.onboarding) && <OnboardingSchritte key={tick} leadId={leadId} onChange={aendern} />}
      <AusGespraechen leadId={leadId} aktualisierung={tick} />
      <Sprachnotiz leadId={leadId} onChange={aendern} />
    </div>
  );
}
